import axios from "axios";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  useCartDeleteMutation,
  useGetCartProductsQuery,
  useGetTotalCartQuery,
  usePlaceOrderMutation,
  useUpdateCartQtyMutation,
} from "../redux/feature/slice/service-api";
import { FaRegTrashAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Title from "../components/helmet/title";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Checkout = () => {
  const [ip, setIp] = useState();
  const navigate = useNavigate();

  const { data: cart, refetch } = useGetCartProductsQuery(ip);
  const { refetch: countRefetch } = useGetTotalCartQuery(ip);
  const [deleteCart] = useCartDeleteMutation();
  const [updateCart] = useUpdateCartQtyMutation();
  const [confirmOrder] = usePlaceOrderMutation();

  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchIpAddress = async () => {
      try {
        const response = await axios.get("https://api.ipify.org?format=json");
        setIp(response.data.ip);
      } catch (error) {
        console.error("Error fetching IP address:", error);
        setIp("127.0.0.1");
      }
    };
    fetchIpAddress();
  }, []);

  // GA4 Datalayer - Begin Checkout
  useEffect(() => {
    if (cart?.data?.carts && Array.isArray(cart.data.carts)) {
      const items = cart.data.carts.map(cartItem => ({
        item_name: cartItem.product.name,
        item_id: cartItem.product.id,
        price: cartItem.product.discount_price || cartItem.product.regular_price,
        item_brand: "Unknown",
        item_category: cartItem.product.category.name,
        quantity: cartItem.qty,
      }));

      const total_price = cart.data.carts.reduce(
        (sum, cartItem) => sum + cartItem.price * cartItem.qty,
        0
      );

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: "begin_checkout",
        ecommerce: {
          currency: "BDT",
          value: total_price,
          items: items,
        },
      });
    }
  }, [cart]);

  // React Hook Form
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      delivery: "100",
    },
  });

  const deliveryValue = watch("delivery");

  const handleDeliveryChange = (event) => {
    setValue("delivery", event.target.value);
  };

  const productsDetails = cart?.data?.carts
    ? cart.data.carts.map((c) => ({
        id: c.product.id,
        color: c.color || null,
        price: c.price,
        qty: c.qty,
        size: c.size || null,
      }))
    : [];

  const TotalQuantity = cart?.data?.carts
    ? cart.data.carts.reduce((total, currentProduct) => total + currentProduct.qty, 0).toString()
    : "0";

  const onSubmit = async (data) => {
    const subTotal = (cart?.data?.subTotal + Number(deliveryValue)).toString();

    const order = {
      ip_address: ip,
      customer_name: data.name,
      customer_phone: data.phone,
      delivery_area: data.delivery,
      customer_address: data.address,
      price: subTotal,
      product_quantity: TotalQuantity,
      payment_type: "cod",
      order_type: "Website",
      products: productsDetails,
    };

    if (productsDetails.length > 0) {
      try {
        const res = await confirmOrder(order);
        if (res?.data?.success === true) {
          const orderId = res.data.data.orderId;
          refetch();
          countRefetch();
          toast.success("Order placed successfully");
          navigate(`/success-order/${orderId}`);
        }
      } catch (error) {
        toast.error("Failed to place order");
      }
    } else {
      toast.error("Please add products to cart");
    }

    reset();
  };

  const handleQtyChange = async (id, currentQty, type) => {
    const newQty = type === "inc" ? currentQty + 1 : currentQty - 1;
    if (newQty < 1) {
      toast.warning("Minimum quantity is 1");
      return;
    }

    try {
      await updateCart({ id, qty: newQty }).unwrap();
      toast.success("Quantity updated");
      refetch();
      countRefetch();
    } catch (error) {
      toast.error("Failed to update quantity");
    }
  };

  const deleteHandler = async (id) => {
    try {
      const res = await deleteCart(id).unwrap();
      toast.success(res?.message || "Product removed");
      refetch();
      countRefetch();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  return (
    <section className="container mx-auto px-2 lg:px-20 my-10 lg:py-20 mt-14 md:mt-0">
      <ToastContainer position="top-right" autoClose={3000} />
      <Title title={"Checkout"} content={"This is checkout page"} />
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="lg:flex justify-between items-start gap-5 mx-auto min-h-full"
        id="checkout_form"
      >
        {/* Left - Billing Form */}
        <div className="w-full xl:w-2/3 rounded-md bg-white shadow-xl py-2 lg:py-10 px-2 lg:px-6 my-6 lg:my-0">
          <h3 className="text-xl lg:text-2xl font-semibold py-2">
            Billing / Shipping Details
          </h3>
          <div className="lg:flex justify-between items-center gap-4 my-4">
            <div className="w-full">
              <input
                type="text"
                placeholder="Enter full Name*"
                className="bg-base lg:py-4 w-full border border-base_300 text-dark rounded-lg focus:ring-primary focus:border-primary block p-2.5"
                {...register("name", { required: true })}
              />
              {errors.name && <small className="text-danger">Name is required</small>}
            </div>
            <div className="w-full">
              <input
                type="tel"
                placeholder="Phone*"
                className="bg-base w-full lg:py-4 border border-base_300 text-dark rounded-lg focus:ring-primary focus:border-primary block p-2.5"
                {...register("phone", { required: true })}
              />
              {errors.phone && <small className="text-danger">Phone is required</small>}
            </div>
          </div>
          <div className="my-4">
            <textarea
              placeholder="Enter your full Address*"
              className="bg-base w-full lg:py-4 border border-base_300 text-dark rounded-lg focus:ring-primary focus:border-primary block p-2.5"
              {...register("address", { required: true })}
            ></textarea>
            {errors.address && <small className="text-danger">Address is required</small>}
          </div>
          <div className="my-4">
            <div className="mb-2">
              <input
                type="radio"
                value="60"
                id="inside"
                {...register("delivery", { required: true })}
                defaultChecked
                onChange={handleDeliveryChange}
              />
              <label className="ml-2" for="inside">Inside Dhaka (60 TK.)</label>
            </div>
            <div>
              <input
                type="radio"
                value="120"
                id="outside"
                {...register("delivery", { required: true })}
                onChange={handleDeliveryChange}
              />
              <label className="ml-2" for="outside">Outside Dhaka (120 TK.)</label>
            </div>
          </div>
        </div>

        {/* Right - Cart Summary */}
        <div className="w-full xl:w-1/3 bg-white rounded-md shadow-xl p-2 lg:p-6 h-fit">
          <div className="overflow-y-scroll h-[200px]">
            {cart?.data?.carts?.length > 0 ? (
              cart.data.carts.map((product) => (
                <div
                  className="flex justify-between items-center mx-auto gap-4 my-4"
                  key={product.id}
                >
                  <img src={product.product.imageUrl} alt={product.product.name} className="w-20" />
                  <div className="w-72">
                    <h4 className="line-clamp-1">{product.product.name}</h4>
                    <p className="text-primary font-medium">
                      {product.price} TK.
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleQtyChange(product.id, product.qty, "dec")}
                        className="px-2 py-1 bg-gray-200 rounded"
                      >
                        -
                      </button>
                      <span>{product.qty}</span>
                      <button
                        type="button"
                        onClick={() => handleQtyChange(product.id, product.qty, "inc")}
                        className="px-2 py-1 bg-gray-200 rounded"
                      >
                        +
                      </button>

                    </div>
                    <p className="text-sm">
                      Size: {product.size || "N/A"} | Color: {product.color || "N/A"}
                    </p>
                  </div>
                  <button
                    onClick={() => deleteHandler(product.id)}
                    className="text-primary active:text-danger"
                  >
                    <FaRegTrashAlt />
                  </button>
                </div>
              ))
            ) : (
              <p className="text-center">You haven't added any products!</p>
            )}
          </div>

          <hr className="border my-2" />
          <div>
            <div className="flex justify-between my-1">
              <p className="font-semibold">Sub Total</p>
              <p>{cart?.data?.subTotal} Tk.</p>
            </div>
            <div className="flex justify-between my-1">
              <p className="font-semibold">Delivery Charge</p>
              <p>{deliveryValue} Tk.</p>
            </div>
            <hr className="border my-2" />
            <div className="flex justify-between">
              <p className="font-semibold">Grand Total</p>
              <p>{cart?.data?.subTotal + Number(deliveryValue)} Tk.</p>
            </div>
            <div className="mt-4">
              <p className="my-1 font-semibold">Select Payment Method</p>
              <input
                type="radio"
                name="paymentMethod"
                value="Cash on Delivery"
                defaultChecked
              />
              <label className="ml-2">Cash on Delivery</label>
              <button className="bg-primary py-2 w-full mt-6 text-white rounded-md">
                Place Order <i className="fa-solid fa-right-from-bracket ml-2"></i>
              </button>
            </div>
          </div>
        </div>
      </form>
    </section>
  );
};

export default Checkout;
